/**
 * Date formatting for the UI. Browsers ship incomplete Uzbek locale data — `Intl` renders
 * `uz` dates like "2026 M10 12" — so Uzbek uses explicit month names; Russian uses `Intl`.
 */
const UZ_MONTHS = [
  "yanvar",
  "fevral",
  "mart",
  "aprel",
  "may",
  "iyun",
  "iyul",
  "avgust",
  "sentabr",
  "oktabr",
  "noyabr",
  "dekabr",
] as const;

/** Tashkent is UTC+5 all year (no daylight saving). */
const TASHKENT_OFFSET_HOURS = 5;
const API_DATE = /^(\d{2})-(\d{2})-(\d{4})(?: (\d{2}):(\d{2}):(\d{2}))?$/;

/**
 * Parses a date of the company API standard: "16-10-2026" or "16-10-2026 14:30:00" (Tashkent time). Other strings
 * (ISO dates made in the browser) go to `new Date`.
 */
export function parseApiDate(text: string): Date {
  const match = API_DATE.exec(text);
  if (!match) return new Date(text);
  const [, day, month, year, hours = "12", minutes = "00", seconds = "00"] = match;
  // a date without time is noon in Tashkent, so it is the same day in every time zone
  return new Date(
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours) - TASHKENT_OFFSET_HOURS,
      Number(minutes),
      Number(seconds),
    ),
  );
}

/** A date for the API: "16-10-2026" from the `yyyy-mm-dd` of a native date input. */
export function toApiDate(inputValue: string): string {
  const [year, month, day] = inputValue.split("-");
  return `${day ?? ""}-${month ?? ""}-${year ?? ""}`;
}

/** Day, month (1-12) and year of an API date or an instant, in Tashkent time. */
function tashkentParts(text: string): { day: number; month: number; year: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Tashkent",
    year: "numeric",
    month: "numeric",
    day: "numeric",
  }).formatToParts(parseApiDate(text));
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((p) => p.type === type)?.value);
  return { day: get("day"), month: get("month"), year: get("year") };
}

/** "12 октября 2026 г." / "12-oktabr, 2026-yil" (in Tashkent time). */
export function formatLongDate(iso: string, language: string): string {
  const date = parseApiDate(iso);
  if (language.startsWith("uz")) {
    const { day, month, year } = tashkentParts(iso);
    return `${day}-${UZ_MONTHS[month - 1] ?? ""}, ${year}-yil`;
  }
  return new Intl.DateTimeFormat("ru-RU", { dateStyle: "long", timeZone: "Asia/Tashkent" }).format(
    date,
  );
}

/** "12 октября" / "12-oktabr" — for near dates where the year is obvious ("до 5 августа"). */
export function formatDayMonth(iso: string, language: string): string {
  if (language.startsWith("uz")) {
    const { day, month } = tashkentParts(iso);
    return `${day}-${UZ_MONTHS[month - 1] ?? ""}`;
  }
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    timeZone: "Asia/Tashkent",
  }).format(parseApiDate(iso));
}

/** "сент" / "sentabr" — a month of an API date ("01-09-2026"), for chart labels. */
export function formatShortMonth(iso: string, language: string): string {
  const { month } = tashkentParts(iso);
  if (language.startsWith("uz")) return UZ_MONTHS[month - 1] ?? "";
  return new Intl.DateTimeFormat("ru-RU", { month: "short", timeZone: "Asia/Tashkent" })
    .format(parseApiDate(iso))
    .replace(/\.$/, "");
}

/** "14:05" — the time of an instant in Tashkent; the same in both languages. */
export function formatTime(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: "Asia/Tashkent",
  }).format(parseApiDate(iso));
}

/** "05.07.2026" — tables and compact lists; the same in both languages. */
export function formatShortDate(iso: string): string {
  const { day, month, year } = tashkentParts(iso);
  return `${String(day).padStart(2, "0")}.${String(month).padStart(2, "0")}.${year}`;
}

/**
 * Whole days from today to an API date, both in Tashkent ("05-11-2026"): 0 = today, negative = in the past.
 * Used for "due in 3 days" and "overdue" states.
 */
export function daysUntil(apiDate: string, now: Date = new Date()): number {
  const { day, month, year } = tashkentParts(apiDate);
  const today = tashkentParts(now.toISOString());
  const target = Date.UTC(year, month - 1, day);
  const start = Date.UTC(today.year, today.month - 1, today.day);
  return Math.round((target - start) / 86_400_000);
}
